import { X, FileText, ExternalLink } from "lucide-react";

interface FilePreviewModalProps {
  url: string;
  fileName?: string;
  mimeType?: string;
  onClose: () => void;
}

function detectFileType(url: string, mimeType?: string): "image" | "pdf" | "other" {
  if (mimeType) {
    if (mimeType.startsWith("image/")) return "image";
    if (mimeType === "application/pdf") return "pdf";
    return "other";
  }
  const clean = url.split("?")[0].toLowerCase();
  if (/\.(jpg|jpeg|png|gif|webp|svg)$/.test(clean)) return "image";
  if (/\.pdf$/.test(clean)) return "pdf";
  return "other";
}

export default function FilePreviewModal({ url, fileName, mimeType, onClose }: FilePreviewModalProps) {
  const fileType = detectFileType(url, mimeType);
  const displayName = fileName || url.split("/").pop()?.split("?")[0] || "File";

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) onClose();
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
      onClick={handleBackdropClick}
    >
      <div className="relative flex flex-col w-full max-w-3xl max-h-[90vh] bg-white rounded-2xl shadow-2xl overflow-hidden">
        <div className="flex items-center justify-between gap-3 px-5 py-4 border-b border-slate-100 shrink-0">
          <div className="flex items-center gap-2 min-w-0">
            <FileText className="w-4 h-4 text-slate-400 shrink-0" />
            <span className="text-sm font-bold text-slate-800 truncate">{displayName}</span>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1 text-[10px] font-black uppercase tracking-wider text-slate-500 hover:text-[#001B44] bg-slate-50 hover:bg-slate-100 border border-slate-200 px-2 py-1 rounded-lg transition-all"
            >
              <ExternalLink className="w-3 h-3" />
              Open
            </a>
            <button
              type="button"
              onClick={onClose}
              className="w-7 h-7 flex items-center justify-center rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-all"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-auto bg-slate-50 min-h-0">
          {fileType === "image" && (
            <div className="flex items-center justify-center p-6 min-h-[300px]">
              <img
                src={url}
                alt={displayName}
                className="max-w-full max-h-[70vh] object-contain rounded-lg shadow"
              />
            </div>
          )}
          {fileType === "pdf" && (
            <div className="flex flex-col h-[70vh]">
              <object
                data={url}
                type="application/pdf"
                className="w-full flex-1 border-0"
              >
                <div className="flex flex-col items-center justify-center gap-4 py-16 text-center h-full">
                  <FileText className="w-12 h-12 text-slate-300" />
                  <p className="text-sm font-bold text-slate-500">
                    PDF preview is not supported in this browser.
                  </p>
                  <a
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-sm font-bold text-[#001B44] hover:underline"
                  >
                    <ExternalLink className="w-4 h-4" />
                    Open PDF in new tab
                  </a>
                </div>
              </object>
            </div>
          )}
          {fileType === "other" && (
            <div className="flex flex-col items-center justify-center gap-4 py-16 text-center">
              <FileText className="w-12 h-12 text-slate-300" />
              <p className="text-sm font-bold text-slate-500">Preview not available for this file type.</p>
              <a
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-sm font-bold text-[#001B44] hover:underline"
              >
                <ExternalLink className="w-4 h-4" />
                Open file
              </a>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
